import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EphanService from 'services/EphanService';

const initialState = {
  dancers: [],
  dancersMeta: null,
  coaches: [],
  coachesMeta: null,
  judges: [],
  judgesMeta: null,
  clubs: [],
  clubsMeta: null,
  dancerClasses: null,
  countries: null,
  statuses: null,
  error: null,
  isLoading: false,
  isFetchingMore: false,
};

const transformDancers = (items) =>
  (Array.isArray(items) ? items : []).map((dancer) => ({
    ...dancer,
    name: (dancer.nameEn || dancer.name || '').trim(),
    surname: (dancer.surnameEn || dancer.surname || '').trim(),
    nameNative: (dancer.name || '').trim(),
    surnameNative: (dancer.surname || '').trim(),
    nameEn: (dancer.nameEn || '').trim(),
    surnameEn: (dancer.surnameEn || '').trim(),
    photo: dancer.avatar ? { url: dancer.avatar } : null,
    level: dancer.danceClass,
    country: typeof dancer.country === 'object' ? dancer.country?.name || '' : dancer.country || '',
    status: 'Active',
  }));

const transformCoaches = (items) =>
  (Array.isArray(items) ? items : []).map((coach) => ({
    ...coach,
    name: (coach.nameEn || coach.name || '').trim(),
    surname: (coach.surnameEn || coach.surname || '').trim(),
    nameNative: (coach.name || '').trim(),
    surnameNative: (coach.surname || '').trim(),
    nameEn: (coach.nameEn || '').trim(),
    surnameEn: (coach.surnameEn || '').trim(),
    photo: coach.avatar ? { url: coach.avatar } : null,
    level: coach.highestClass,
    country: typeof coach.country === 'object' ? coach.country?.name || '' : coach.country || '',
    status: 'Active',
  }));

const transformJudges = (items) =>
  (Array.isArray(items) ? items : []).map((judge) => ({
    ...judge,
    name: (judge.nameEn || judge.name || '').trim(),
    surname: (judge.surnameEn || judge.surname || '').trim(),
    nameNative: (judge.name || '').trim(),
    surnameNative: (judge.surname || '').trim(),
    nameEn: (judge.nameEn || '').trim(),
    surnameEn: (judge.surnameEn || '').trim(),
    photo: judge.avatar ? { url: judge.avatar } : null,
    level: judge.judgeCategory,
    country: typeof judge.country === 'object' ? judge.country?.name || '' : judge.country || '',
    status: 'Active',
  }));

const transformClubs = (items) =>
  (Array.isArray(items) ? items : []).map((club) => ({
    ...club,
    name: (club.name || '').trim(),
    photo: club.logo ? { url: club.logo } : null,
    country: typeof club.country === 'object' ? club.country?.name || '' : club.country || '',
  }));

export const getDancers = createAsyncThunk('dancers/getDancers', async (params = {}, { rejectWithValue }) => {
  const { result, error } = await EphanService.getDancers(params);
  if (result) {
    const rawItems = Array.isArray(result) ? result : result.data || [];
    const meta = result.meta || null;
    const items = transformDancers(rawItems);
    return { items, meta, isAppend: Boolean(params.page && params.page > 1) };
  }
  return rejectWithValue(error || 'Failed to fetch dancers');
});

export const getJudges = createAsyncThunk('judges/getJudges', async (params = {}, { rejectWithValue }) => {
  const { result, error } = await EphanService.getJudges(params);
  if (result) {
    const rawItems = Array.isArray(result) ? result : result.data || [];
    const meta = result.meta || null;
    const items = transformJudges(rawItems);
    return { items, meta, isAppend: Boolean(params.page && params.page > 1) };
  }
  return rejectWithValue(error || 'Failed to fetch judges');
});

export const getCoaches = createAsyncThunk('coaches/getCoaches', async (params = {}, { rejectWithValue }) => {
  const { result, error } = await EphanService.getCoaches(params);
  if (result) {
    const rawItems = Array.isArray(result) ? result : result.data || [];
    const meta = result.meta || null;
    const items = transformCoaches(rawItems);
    return { items, meta, isAppend: Boolean(params.page && params.page > 1) };
  }
  return rejectWithValue(error || 'Failed to fetch coaches');
});

export const getClubs = createAsyncThunk('clubs/getClubs', async (params = {}, { rejectWithValue }) => {
  const { result, error } = await EphanService.getClubs(params);
  if (result) {
    const rawItems = Array.isArray(result) ? result : result.data || [];
    const meta = result.meta || null;
    const items = transformClubs(rawItems);
    return { items, meta, isAppend: Boolean(params.page && params.page > 1) };
  }
  return rejectWithValue(error || 'Failed to fetch clubs');
});

export const getDancerClasses = createAsyncThunk('dancerClasses/getDancerClasses', async () => ({}));
export const getStatuses = createAsyncThunk('statuses/getStatuses', async () => ({}));
export const getCountries = createAsyncThunk('countries/getCountries', async (_, { rejectWithValue }) => {
  const { result, error } = await EphanService.getRegistryFilters();
  if (result) {
    const rawCountries = result.countries || result || [];
    if (Array.isArray(rawCountries)) {
      return rawCountries
        .map((c) => (typeof c === 'object' ? c.name || c.title || c.country || '' : String(c)))
        .filter(Boolean);
    }
    if (typeof rawCountries === 'object' && rawCountries !== null) {
      return Object.values(rawCountries)
        .map((c) => (typeof c === 'object' ? c.name || c.title || c.country || '' : String(c)))
        .filter(Boolean);
    }
  }
  return rejectWithValue(error || 'Failed to fetch countries');
});

const ephanSlice = createSlice({
  name: 'ephan',
  initialState,
  extraReducers: (builder) => {
    builder
      .addCase(getCountries.fulfilled, (state, { payload }) => {
        state.countries = payload;
      })
      .addCase(getDancers.pending, (state, action) => {
        const isAppend = action.meta.arg?.page > 1;
        if (isAppend) {
          state.isFetchingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(getDancers.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.dancersMeta = payload.meta;
        if (payload.isAppend) {
          const existingIds = new Set(state.dancers.map((d) => d.id));
          const newItems = payload.items.filter((d) => !existingIds.has(d.id));
          state.dancers.push(...newItems);
        } else {
          state.dancers = payload.items;
        }
      })
      .addCase(getDancers.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.error = payload;
      });

    builder
      .addCase(getJudges.pending, (state, action) => {
        const isAppend = action.meta.arg?.page > 1;
        if (isAppend) {
          state.isFetchingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(getJudges.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.judgesMeta = payload.meta;
        if (payload.isAppend) {
          const existingIds = new Set(state.judges.map((j) => j.id));
          const newItems = payload.items.filter((j) => !existingIds.has(j.id));
          state.judges.push(...newItems);
        } else {
          state.judges = payload.items;
        }
      })
      .addCase(getJudges.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.error = payload;
      });

    builder
      .addCase(getCoaches.pending, (state, action) => {
        const isAppend = action.meta.arg?.page > 1;
        if (isAppend) {
          state.isFetchingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(getCoaches.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.coachesMeta = payload.meta;
        if (payload.isAppend) {
          const existingIds = new Set(state.coaches.map((c) => c.id));
          const newItems = payload.items.filter((c) => !existingIds.has(c.id));
          state.coaches.push(...newItems);
        } else {
          state.coaches = payload.items;
        }
      })
      .addCase(getCoaches.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.error = payload;
      });

    builder
      .addCase(getClubs.pending, (state, action) => {
        const isAppend = action.meta.arg?.page > 1;
        if (isAppend) {
          state.isFetchingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(getClubs.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.clubsMeta = payload.meta;
        if (payload.isAppend) {
          const existingIds = new Set(state.clubs.map((c) => c.id));
          const newItems = payload.items.filter((c) => !existingIds.has(c.id));
          state.clubs.push(...newItems);
        } else {
          state.clubs = payload.items;
        }
      })
      .addCase(getClubs.rejected, (state, { payload }) => {
        state.isLoading = false;
        state.isFetchingMore = false;
        state.error = payload;
      });
  },
});

export default ephanSlice.reducer;
